import { useState, useMemo, useRef, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useVirtualizer } from '@tanstack/react-virtual'
import {
  Search,
  ChevronDown,
  Phone,
  MessageSquare,
  ClipboardList,
  CheckCircle2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { useContacts } from '@/hooks/use-contacts'
import { useCircles } from '@/hooks/use-circles'
import { useLabels } from '@/hooks/use-labels'
import { useMultiSelect } from '@/hooks/use-multi-select'
import BatchActionsToolbar from '@/components/BatchActionsToolbar'
import { useStreaks } from '@/hooks/use-gamification'
import { StreakBadge } from '@/components/StreakBadge'
import type { Contact } from '@/lib/types'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')

const AVATAR_COLORS = [
  '#ef4444',
  '#f97316',
  '#f59e0b',
  '#84cc16',
  '#22c55e',
  '#14b8a6',
  '#06b6d4',
  '#3b82f6',
  '#0F2E28',
  '#FF6F61',
  '#a855f7',
  '#d946ef',
  '#ec4899',
  '#f43f5e',
]

function hashName(name: string): number {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return Math.abs(hash)
}

function avatarColor(contact: Contact): string {
  const name = `${contact.firstName}${contact.lastName ?? ''}`
  return AVATAR_COLORS[hashName(name) % AVATAR_COLORS.length]
}

function initials(contact: Contact): string {
  const first = contact.firstName?.charAt(0) ?? ''
  const last = contact.lastName?.charAt(0) ?? ''
  return (first + last).toUpperCase() || '?'
}

function fullName(contact: Contact): string {
  return [contact.firstName, contact.lastName].filter(Boolean).join(' ')
}

function relativeTime(dateStr: string | null): string {
  if (!dateStr) return ''
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  if (Number.isNaN(then)) return ''

  const diffMs = now - then
  if (diffMs < 0) return ''

  const days = Math.floor(diffMs / 86_400_000)
  const weeks = Math.floor(days / 7)
  const months = Math.floor(days / 30)
  const years = Math.floor(days / 365)

  if (days === 0) return 'today'
  if (days === 1) return '1d'
  if (days < 7) return `${days}d`
  if (weeks === 1) return '1w'
  if (weeks < 4) return `${weeks}w`
  if (months === 1) return '1mo'
  if (months < 12) return `${months}mo`
  if (years === 1) return '1y'
  return `${years}y`
}

const FREQUENCY_DAYS: Record<string, number> = {
  weekly: 7,
  biweekly: 14,
  monthly: 30,
  quarterly: 90,
  yearly: 365,
}

function isOverdue(contact: Contact): boolean {
  const freq = contact.contactFrequency
  if (!freq || freq === 'none') return false
  const maxDays = FREQUENCY_DAYS[freq]
  if (!maxDays) return false
  if (!contact.lastContactedAt) return true // never contacted but has frequency = overdue

  const diffMs = Date.now() - new Date(contact.lastContactedAt).getTime()
  return diffMs > maxDays * 86_400_000
}

type SortKey = 'name' | 'lastContacted' | 'recentlyAdded'

const SORT_OPTIONS: { key: SortKey; labelKey: string; sort: string; order: string }[] = [
  { key: 'name', labelKey: 'contacts.sortName', sort: 'name', order: 'asc' },
  {
    key: 'lastContacted',
    labelKey: 'contacts.sortLastContacted',
    sort: 'last_contacted_at',
    order: 'desc',
  },
  {
    key: 'recentlyAdded',
    labelKey: 'contacts.sortRecentlyAdded',
    sort: 'created_at',
    order: 'desc',
  },
]

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ContactSkeleton() {
  return (
    <div className="flex animate-pulse items-center gap-3 px-6 py-3">
      <div className="bg-surface-container-high h-10 w-10 shrink-0 rounded-full" />
      <div className="flex-1 space-y-2">
        <div className="bg-surface-container-high h-4 w-32 rounded" />
        <div className="bg-surface-container-high h-3 w-20 rounded" />
      </div>
      <div className="bg-surface-container-high h-3 w-8 rounded" />
    </div>
  )
}

function AvatarCircle({ contact }: { contact: Contact }) {
  if (contact.avatarUrl) {
    return (
      <img
        src={contact.avatarUrl}
        alt={fullName(contact)}
        className="h-10 w-10 shrink-0 rounded-full object-cover"
      />
    )
  }
  const bg = avatarColor(contact)
  return (
    <div
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white"
      style={{ backgroundColor: bg }}
    >
      {initials(contact)}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Swipeable Contact Row
// ---------------------------------------------------------------------------

const SWIPE_THRESHOLD = 60

interface ContactRowProps {
  contact: Contact
  isSelected?: boolean
  isSelecting?: boolean
  isChecked?: boolean
  onToggleSelect?: (id: string) => void
  onLongPress?: () => void
  streak?: number
}

function ContactRow({
  contact,
  isSelected,
  isSelecting,
  isChecked,
  onToggleSelect,
  onLongPress,
  streak,
}: ContactRowProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const relative = relativeTime(contact.lastContactedAt)
  const overdue = isOverdue(contact)

  const touchStartX = useRef(0)
  const touchCurrentX = useRef(0)
  const [swipeOffset, setSwipeOffset] = useState(0)
  const [swiping, setSwiping] = useState(false)
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      touchStartX.current = e.touches[0].clientX
      touchCurrentX.current = e.touches[0].clientX
      setSwiping(true)

      // Long press to enter select mode
      if (!isSelecting && onLongPress) {
        longPressTimer.current = setTimeout(() => {
          onLongPress()
          onToggleSelect?.(contact.id)
        }, 500)
      }
    },
    [isSelecting, onLongPress, onToggleSelect, contact.id],
  )

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      // Cancel long press on move
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current)
        longPressTimer.current = null
      }

      if (!swiping) return
      touchCurrentX.current = e.touches[0].clientX
      const diff = touchStartX.current - touchCurrentX.current
      // Only allow left swipe (positive diff), disable swipe in select mode
      if (isSelecting) return
      setSwipeOffset(Math.max(0, Math.min(diff, 180)))
    },
    [swiping, isSelecting],
  )

  const handleTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
    setSwiping(false)
    if (swipeOffset > SWIPE_THRESHOLD) {
      setSwipeOffset(180) // snap open
    } else {
      setSwipeOffset(0) // snap closed
    }
  }, [swipeOffset])

  const closeSwipe = useCallback(() => setSwipeOffset(0), [])

  const handleCall = useCallback(() => {
    closeSwipe()
    if (contact.phone) {
      window.open(`tel:${contact.phone}`, '_self')
    }
  }, [contact.phone, closeSwipe])

  const handleMessage = useCallback(() => {
    closeSwipe()
    if (contact.phone) {
      window.open(`sms:${contact.phone}`, '_self')
    } else if (contact.email) {
      window.open(`mailto:${contact.email}`, '_self')
    }
  }, [contact.phone, contact.email, closeSwipe])

  const handleLogInteraction = useCallback(() => {
    closeSwipe()
    navigate(`/contacts/${contact.id}/interaction`)
  }, [contact.id, navigate, closeSwipe])

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (swipeOffset > 0) {
        e.preventDefault()
        closeSwipe()
        return
      }
      if (isSelecting) {
        e.preventDefault()
        onToggleSelect?.(contact.id)
      }
    },
    [swipeOffset, closeSwipe, isSelecting, onToggleSelect, contact.id],
  )

  return (
    <div className="relative overflow-hidden">
      {/* Swipe actions background (hidden in select mode) */}
      {!isSelecting && (
        <div className="absolute inset-y-0 right-0 flex items-stretch">
          <button
            onClick={handleCall}
            className="bg-chartreuse text-chartreuse-foreground flex w-[60px] items-center justify-center"
            aria-label={t('contacts.swipeCall', 'Call')}
          >
            <Phone className="h-5 w-5" />
          </button>
          <button
            onClick={handleMessage}
            className="bg-lilac text-lilac-foreground flex w-[60px] items-center justify-center"
            aria-label={t('contacts.swipeMessage', 'Message')}
          >
            <MessageSquare className="h-5 w-5" />
          </button>
          <button
            onClick={handleLogInteraction}
            className="bg-coral text-coral-foreground flex w-[60px] items-center justify-center"
            aria-label={t('contacts.swipeLog', 'Log')}
          >
            <ClipboardList className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Main contact row */}
      <Link
        to={isSelecting ? '#' : `/contacts/${contact.id}`}
        className={cn(
          'relative flex items-center gap-3 px-6 py-3 transition-transform',
          isSelected || isChecked
            ? 'bg-primary/10'
            : 'bg-background active:bg-surface-container-high/60',
        )}
        style={{
          transform: isSelecting ? undefined : `translateX(-${swipeOffset}px)`,
          transition: swiping ? 'none' : 'transform 0.2s ease-out',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleClick}
      >
        {/* Checkbox in select mode */}
        {isSelecting && (
          <div
            className={cn(
              'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
              isChecked ? 'border-primary bg-primary' : 'border-outline-variant/50 bg-transparent',
            )}
          >
            {isChecked && <CheckCircle2 className="h-5 w-5 text-white" />}
          </div>
        )}
        <div className="relative">
          <AvatarCircle contact={contact} />
          {overdue && (
            <span className="bg-coral border-background absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-foreground truncate text-sm font-medium">{fullName(contact)}</p>
          {contact.company && (
            <p className="text-muted-foreground truncate text-xs">{contact.company}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {streak != null && streak > 0 && <StreakBadge streak={streak} />}
          {relative && (
            <span
              className={cn(
                'text-xs',
                overdue ? 'text-coral font-medium' : 'text-muted-foreground',
              )}
            >
              {relative}
            </span>
          )}
        </div>
      </Link>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const PULL_THRESHOLD = 60

interface ContactsPageProps {
  /** When true, rendered inside the desktop split-panel left column. */
  embedded?: boolean
  /** The currently-selected contact id (for highlighting in split view). */
  selectedContactId?: string
}

export default function ContactsPage({ embedded, selectedContactId }: ContactsPageProps = {}) {
  const { t } = useTranslation()

  // Local state
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebouncedValue(query, 300)
  const [activeCircle, setActiveCircle] = useState<string | null>(null)
  const [activeLabel, setActiveLabel] = useState<string | null>(null)
  const [sortKey, setSortKey] = useState<SortKey>('name')
  const [sortOpen, setSortOpen] = useState(false)

  // Pull-to-refresh state
  const [pullDistance, setPullDistance] = useState(0)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const pullStartY = useRef(0)
  const isPulling = useRef(false)

  const listRef = useRef<HTMLDivElement>(null)

  const sortOption = SORT_OPTIONS.find((o) => o.key === sortKey)!

  // Data fetching
  const { data: circlesData } = useCircles()
  const circles = Array.isArray(circlesData) ? circlesData : []
  const { data: labelsData } = useLabels()
  const labels = Array.isArray(labelsData) ? labelsData : []
  const { data: streaksData } = useStreaks()
  const streakMap = useMemo(() => {
    const map = new Map<string, number>()
    for (const s of streaksData?.streaks ?? []) {
      map.set(s.contactId, s.currentStreak)
    }
    return map
  }, [streaksData])

  const { data, isLoading, isError, refetch } = useContacts({
    sort: sortOption.sort,
    order: sortOption.order,
    q: debouncedQuery || undefined,
    circle_id: activeCircle ?? undefined,
    label_id: activeLabel ?? undefined,
    limit: 500,
  })

  const contacts = Array.isArray(data?.items) ? data.items : []

  // Multi-select state
  const multiSelect = useMultiSelect(contacts)

  // Group contacts by first letter for alphabet sidebar
  const grouped = useMemo(() => {
    const map: Record<string, Contact[]> = {}
    for (const c of contacts) {
      const letter = (c.firstName?.charAt(0) ?? '?').toUpperCase()
      const key = /[A-Z]/.test(letter) ? letter : '#'
      if (!map[key]) map[key] = []
      map[key].push(c)
    }
    return map
  }, [contacts])

  const activeLetters = useMemo(() => new Set(Object.keys(grouped)), [grouped])

  // Flatten grouped contacts into a single list for virtualization
  type VirtualItem = { type: 'header'; letter: string } | { type: 'contact'; contact: Contact }

  const flatItems = useMemo<VirtualItem[]>(() => {
    if (sortKey !== 'name') {
      return contacts.map((contact) => ({ type: 'contact' as const, contact }))
    }
    const items: VirtualItem[] = []
    for (const letter of ALPHABET.concat('#')) {
      if (!grouped[letter]) continue
      items.push({ type: 'header', letter })
      for (const contact of grouped[letter]) {
        items.push({ type: 'contact', contact })
      }
    }
    return items
  }, [sortKey, contacts, grouped])

  // Map letter -> index in flatItems for alphabet sidebar scrollTo
  const letterIndexMap = useMemo(() => {
    const map: Record<string, number> = {}
    for (let i = 0; i < flatItems.length; i++) {
      const item = flatItems[i]
      if (item.type === 'header') {
        map[item.letter] = i
      }
    }
    return map
  }, [flatItems])

  const virtualizer = useVirtualizer({
    count: flatItems.length,
    getScrollElement: () => listRef.current,
    estimateSize: (index) => (flatItems[index].type === 'header' ? 28 : 64),
    overscan: 10,
  })

  // Scroll to letter
  const scrollToLetter = useCallback(
    (letter: string) => {
      const index = letterIndexMap[letter]
      if (index !== undefined) {
        virtualizer.scrollToIndex(index, { align: 'start' })
      }
    },
    [letterIndexMap, virtualizer],
  )

  // Pull-to-refresh handlers
  const handlePullStart = useCallback((e: React.TouchEvent) => {
    if (listRef.current && listRef.current.scrollTop <= 0) {
      pullStartY.current = e.touches[0].clientY
      isPulling.current = true
    }
  }, [])

  const handlePullMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isPulling.current || isRefreshing) return
      const diff = e.touches[0].clientY - pullStartY.current
      if (diff > 0) {
        setPullDistance(Math.min(diff * 0.5, 100))
      }
    },
    [isRefreshing],
  )

  const handlePullEnd = useCallback(async () => {
    isPulling.current = false
    if (pullDistance >= PULL_THRESHOLD && !isRefreshing) {
      setIsRefreshing(true)
      setPullDistance(PULL_THRESHOLD)
      await refetch()
      setIsRefreshing(false)
    }
    setPullDistance(0)
  }, [pullDistance, isRefreshing, refetch])

  // Close sort dropdown when clicking outside
  const sortRef = useRef<HTMLDivElement>(null)

  return (
    <div className="bg-background flex h-full flex-col">
      {/* Batch actions toolbar */}
      {multiSelect.isSelecting && (
        <BatchActionsToolbar
          selectedIds={multiSelect.selectedIds}
          selectedCount={multiSelect.selectedCount}
          totalCount={contacts.length}
          onSelectAll={multiSelect.selectAll}
          onDeselectAll={multiSelect.deselectAll}
          onExit={multiSelect.exitSelectMode}
        />
      )}

      {/* Header */}
      <div className="shrink-0 space-y-3 px-6 pt-6 pb-2">
        <h1 className="font-heading text-2xl font-bold">{t('contacts.title')}</h1>

        {/* Search bar */}
        <div className="relative">
          <Search className="text-on-surface-variant pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <input
            type="text"
            placeholder={t('contacts.search')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className={cn(
              'border-outline-variant/30 bg-surface-container w-full rounded-xl border py-2.5 pr-4 pl-10 text-sm',
              'placeholder:text-on-surface-variant',
              'focus:ring-primary/30 focus:border-primary focus:ring-2 focus:outline-none',
            )}
          />
        </div>

        {/* Circle filter chips */}
        <div className="flex scrollbar-none gap-2 overflow-x-auto pb-1">
          <button
            onClick={() => {
              setActiveCircle(null)
              setActiveLabel(null)
            }}
            className={cn(
              'shrink-0 rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors',
              activeCircle === null && activeLabel === null
                ? 'bg-primary text-primary-foreground'
                : 'bg-surface-container-high text-foreground',
            )}
          >
            {t('contacts.all', 'All')}
          </button>
          {circles.map((circle) => (
            <button
              key={circle.id}
              onClick={() => {
                setActiveCircle(activeCircle === circle.id ? null : circle.id)
                setActiveLabel(null)
              }}
              className={cn(
                'shrink-0 rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors',
                activeCircle === circle.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-surface-container-high text-foreground',
              )}
            >
              {circle.color && (
                <span
                  className="mr-1.5 inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: circle.color }}
                />
              )}
              {circle.name}
              <span className="ml-1 opacity-60">{circle.contactCount}</span>
            </button>
          ))}
          {labels.map((label) => (
            <button
              key={label.id}
              onClick={() => {
                setActiveLabel(activeLabel === label.id ? null : label.id)
                setActiveCircle(null)
              }}
              className={cn(
                'shrink-0 rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors',
                activeLabel === label.id
                  ? 'text-white'
                  : 'bg-surface-container-high text-foreground',
              )}
              style={
                activeLabel === label.id && label.color
                  ? { backgroundColor: label.color }
                  : activeLabel === label.id
                    ? { backgroundColor: 'hsl(var(--primary))' }
                    : undefined
              }
            >
              {label.name}
            </button>
          ))}
        </div>

        {/* Sort dropdown */}
        <div className="relative flex justify-end" ref={sortRef}>
          <button
            onClick={() => setSortOpen((v) => !v)}
            className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs transition-colors"
          >
            {t(
              sortOption.labelKey,
              sortKey === 'name'
                ? 'Name (A-Z)'
                : sortKey === 'lastContacted'
                  ? 'Last Contacted'
                  : 'Recently Added',
            )}
            <ChevronDown
              className={cn('h-3.5 w-3.5 transition-transform', sortOpen && 'rotate-180')}
            />
          </button>
          {sortOpen && (
            <>
              {/* Backdrop */}
              <div className="fixed inset-0 z-30" onClick={() => setSortOpen(false)} />
              <div className="border-outline-variant/20 bg-surface-container-highest/90 absolute top-full right-0 z-40 mt-1 min-w-[180px] rounded-xl border p-1 shadow-2xl backdrop-blur-xl">
                {SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => {
                      setSortKey(opt.key)
                      setSortOpen(false)
                    }}
                    className={cn(
                      'w-full rounded-lg px-3 py-2 text-left text-sm transition-colors',
                      sortKey === opt.key
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-foreground hover:bg-primary/5',
                    )}
                  >
                    {t(
                      opt.labelKey,
                      opt.key === 'name'
                        ? 'Name (A-Z)'
                        : opt.key === 'lastContacted'
                          ? 'Last Contacted'
                          : 'Recently Added',
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Content area */}
      <div className="relative flex flex-1 overflow-hidden">
        {/* Pull-to-refresh indicator */}
        {pullDistance > 0 && (
          <div
            className="absolute top-0 right-0 left-0 z-20 flex items-center justify-center"
            style={{ height: pullDistance }}
          >
            <div
              className={cn(
                'border-primary h-6 w-6 rounded-full border-2 border-t-transparent',
                isRefreshing && 'animate-spin',
              )}
              style={{
                opacity: Math.min(pullDistance / PULL_THRESHOLD, 1),
                transform: `rotate(${pullDistance * 3}deg)`,
              }}
            />
          </div>
        )}

        {/* Contact list */}
        <div
          ref={listRef}
          className={cn('flex-1 overflow-y-auto', embedded ? 'pb-20' : 'pb-24')}
          style={{
            transform: pullDistance > 0 ? `translateY(${pullDistance}px)` : undefined,
            transition: isPulling.current ? 'none' : 'transform 0.2s ease-out',
          }}
          onTouchStart={handlePullStart}
          onTouchMove={handlePullMove}
          onTouchEnd={handlePullEnd}
        >
          {isLoading && (
            <div>
              {Array.from({ length: 8 }).map((_, i) => (
                <ContactSkeleton key={i} />
              ))}
            </div>
          )}

          {!isLoading && !isError && contacts.length === 0 && (
            <div className="flex flex-1 flex-col items-center justify-center px-6 py-20 text-center">
              <div className="bg-surface-container-high mb-4 flex h-16 w-16 items-center justify-center rounded-full">
                <Search className="text-on-surface-variant h-7 w-7" />
              </div>
              <p className="text-foreground text-lg font-medium">{t('contacts.empty')}</p>
              <p className="text-muted-foreground mt-1 text-sm">
                {t('contacts.addFirst', 'Add your first contact')}
              </p>
            </div>
          )}

          {!isLoading && !isError && contacts.length > 0 && (
            <div
              style={{
                height: `${virtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {virtualizer.getVirtualItems().map((virtualRow) => {
                const item = flatItems[virtualRow.index]
                if (item.type === 'header') {
                  return (
                    <div
                      key={`header-${item.letter}`}
                      data-index={virtualRow.index}
                      ref={virtualizer.measureElement}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                    >
                      <div className="bg-background/90 z-10 px-4 py-1 backdrop-blur-sm">
                        <span className="text-muted-foreground text-xs font-semibold">
                          {item.letter}
                        </span>
                      </div>
                    </div>
                  )
                }
                return (
                  <div
                    key={item.contact.id}
                    data-index={virtualRow.index}
                    ref={virtualizer.measureElement}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    <ContactRow
                      contact={item.contact}
                      isSelected={item.contact.id === selectedContactId}
                      isSelecting={multiSelect.isSelecting}
                      isChecked={multiSelect.isSelected(item.contact.id)}
                      onToggleSelect={multiSelect.toggle}
                      onLongPress={multiSelect.enterSelectMode}
                      streak={streakMap.get(item.contact.id)}
                    />
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Alphabet sidebar */}
        {!isLoading && contacts.length > 0 && sortKey === 'name' && (
          <div className="absolute top-0 right-0 z-20 flex h-full flex-col items-center justify-center py-2 pr-1">
            {ALPHABET.map((letter) => (
              <button
                key={letter}
                onClick={() => scrollToLetter(letter)}
                disabled={!activeLetters.has(letter)}
                className={cn(
                  'flex h-[calc(100%/26)] min-h-[16px] w-5 items-center justify-center text-[10px] leading-none font-medium',
                  activeLetters.has(letter) ? 'text-primary' : 'text-muted-foreground/40',
                )}
              >
                {letter}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
